import React from 'react'
import PropTypes from 'prop-types'
import { withGoogleMap, GoogleMap } from 'react-google-maps'
import InfoBox from './info_box'
import { OverlayView } from './map/overlay_view'
import { MAP_STYLE, OMIT_PARAMS, JAPAN_DEFAULT_OPTIONS, DEFAULT_OPTIONS, PREFECTURE_OFFICES } from './map/constants'
import { validate, getInputValue } from './utils'
import './map.scss'
import CurrencyHelper from '../../common/helpers/currency_helper'

/* global google */
export default class SearchMap extends React.Component {
  static PREFERED_ZOOM = 14 // 推奨ズームレベル

  constructor(props) {
    super(props)

    this.state = {
      highlightedRoomGroup: undefined
    }

    this.ShowGoogleMap = typeof google != 'undefined'
      ? withGoogleMap(({ search = {}, query }) => {
        const component = this
        const options = this.props.match.params.country ? DEFAULT_OPTIONS : JAPAN_DEFAULT_OPTIONS

        return <GoogleMap
          defaultExtraMapTypes={[
            ['detailed', new google.maps.StyledMapType(MAP_STYLE.DETAILED)],
            ['simple', new google.maps.StyledMapType(MAP_STYLE.SIMPLE)]
          ]}
          defaultMapTypeId="detailed"
          defaultOptions={options}
          onIdle={function() { component.onIdle(this) }}
        >
          {this.renderInfoBox()}
        </GoogleMap>
      })
      : React.Fragment
  }

  static propTypes = {
    lang: PropTypes.string.isRequired,
    search: PropTypes.object.isRequired,
    query: PropTypes.object.isRequired,
    actions: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,
    propertyTypes: PropTypes.object.isRequired,
    isTablet: PropTypes.bool.isRequired,
    areaName: PropTypes.string
  }

  static defaultProps = {
    search: {},
    query: {}
  }

  componentDidMount() {
    document.body.classList.add('search-with-map')
    if (this.props.isTablet) { document.body.classList.add('no-scrolling') }
  }

  componentWillUnmount() {
    document.body.classList.remove('search-with-map')
    document.body.classList.remove('no-scrolling')
  }

  componentDidUpdate({ query, match }) {
    if (this.props.isTablet) { document.body.classList.contains('no-scrolling') || document.body.classList.add('no-scrolling') }

    // 検索条件変更時
    if (query != this.props.query) {
      if (this.shouldChangeCenter({ ...match, ...query })) {
        this.setCenter()
      }
      this.reload()
    }
  }

  /**
   * roomGroup再読込
   */
  reload() {
    delete this._query
    // タイルに対応するAPIリクエストキャッシュのmappingをクリア
    this.props.search.tiles.clear()
    // タイルの描画を更新
    this.overlayView.reloadTiles()
  }

  /**
   * 地図検索用のクエリを返す
   */
  get query() {
    if (this._query) { return this._query }
    this._query = validate(this.props.query)

    // 地図検索で使用しないパラメータを削除
    OMIT_PARAMS.forEach(key => (delete this._query[key]))

    if (this.props.match.params.country) {
      this._query.country_code = this.props.match.params.country
    }

    this._query.currency_code = CurrencyHelper.metaCurrencyCode()

    return this._query
  }

  /**
   * google.maps.place.PlaceServiceを生成して返す
   */
  get placeService() {
    if (!this._placeService) {
      if (!this.map) { return }
      this._placeService = new google.maps.places.PlacesService(this.map)
    }
    return this._placeService
  }

  /**
   * google.maps.OverlayViewを生成して返す
   */
  get overlayView() {
    if (!this._overlayView) {
      this._overlayView = new OverlayView(google.maps.OverlayView, {
        'tiles_appended': this.onTilesAppended,
        'room_group_clicked': this.onRoomGroupClicked
      })
    }
    return this._overlayView
  }

  /**
   * 地図のonIdleイベントハンドラ
   */
  onIdle = (map) => {
    this.map = map

    if (!map.getCenter()) {
      return this.setCenter()
    }

    const { overlayView } = this
    overlayView.getMap() || overlayView.setMap(map)
  }

  /**
   * 地図の中央地点を更新すべきかどうか
   */
  shouldChangeCenter(prev) {
    const { lat, lon, destination, prefecture } = this.props.query
    return prev.lat != lat ||
      prev.lon != lon ||
      (prev.destination || prev.prefecture) != (destination || prefecture)
  }

  /**
   * 地図の中央地点のマーカーを設定する
   */
  setCenterMarkerWithTitle(title, latlong) {
    if (typeof this.centerMarker != "undefined") {
      this.centerMarker.setMap(null)
      delete this.centerMarker
    }
    this.centerMarker = new google.maps.Marker({
      position: latlong,
      title: title
    })
    this.centerMarker.setMap(this.map)
  }

  /**
   * 地図の中央地点をセット
   */
  setCenter() {
    // lat,lonパラメータがあればその地点をセット
    const { lat, lon: lng } = this.props.query

    if (lat && lng) {
      // Show a marker title only if destination / prefecture is present
      const centerMarkerTitle = this.props.query.destination || this.props.match.params.prefecture || this.props.query.prefecture || ""
      const centerLatLng = new google.maps.LatLng(lat, lng)
      this.map.setCenter(centerLatLng)
      this.setCenterMarkerWithTitle(centerMarkerTitle, centerLatLng)
      this.map.setZoom(SearchMap.PREFERED_ZOOM)
      return
    }

    // when room_group_id parameter is specified
    if ((this.props.query.room_group_id || this.props.query.property_id) && !this.props.query.destination) {
      this.fitBoundsToRoomGroups()
      return
    }

    // /country/:country/searchの場合
    if (this.props.match.params.country && !this.props.query.destination) {
      const { lat, lon: lng, zoom } = JSON.parse(getInputValue('.js-country-center'))
      this.map.setCenter(new google.maps.LatLng(lat, lng))
      this.map.setZoom(zoom == undefined ? SearchMap.PREFERED_ZOOM : zoom)
      return
    }

    // 都道府県の場合は県庁所在地をセット
    const prefecture = this.props.match.params.prefecture || this.props.query.prefecture
    const spot = this.props.query.destination || this.props.match.params.city || this.props.match.params.spot_id
    if (PREFECTURE_OFFICES[prefecture] && !spot) {
      const centerLatLng = new google.maps.LatLng(PREFECTURE_OFFICES[prefecture])
      this.map.setCenter(centerLatLng)
      this.setCenterMarkerWithTitle(prefecture, centerLatLng)
      this.map.setZoom(SearchMap.PREFERED_ZOOM - 2)
      return
    }

    // 目的地、市区町村、政令指定都市、spot検索の場合
    this.fitBoundsAfterPlaceQuery()
  }

  // Find place from query, then fit bounds to a suitable viewport of the result
  fitBoundsAfterPlaceQuery() {
    this.placeService.findPlaceFromQuery(this.placeQuery, (results, status) => {
      if (status == google.maps.places.PlacesServiceStatus.OK) {
        const [ { geometry: { viewport } } ] = results
        this.map.fitBounds(viewport)
      }
    })
  }

  // Try to fitBounds to contain as many roomGroups as possible
  fitBoundsToRoomGroups() {
    this.props.actions.search.getRoomGroups(this.query).then(({ payload }) => {
      const { LatLng, LatLngBounds } = google.maps
      const { room_groups: roomGroups } = payload || {}

      if (roomGroups) {
        // make a LatLngBounds that contains every roomGroups
        const points = roomGroups.map(({ property: { lat, lon: lng } }) => new LatLng(lat, lng))
        const bounds = points.reduce((b, p) => (b.extend(p)), new LatLngBounds())
        this.map.fitBounds(bounds)

        // Adjust zoom level
        if (this.map.zoom > SearchMap.PREFERED_ZOOM) {
          this.map.setZoom(SearchMap.PREFERED_ZOOM)
        }

        // If any point isn't contained in a viewport after fitBounds (because of zoom level restriction),
        // setCenter to a point of the first hit.
        const viewport = this.map.getBounds()
        if (!points.some(p => viewport.contains(p))) {
          this.map.setCenter(points[0])
        }
      }

      // If the center isn't set finally, fall back to default.
      if (!this.map.getCenter()) {
        this.fitBoundsAfterPlaceQuery()
      }
    })
  }

  /**
   * 場所検索用パラメータ
   * @see https://developers.google.com/maps/documentation/javascript/places
   */
  get placeQuery() {
    const { match, query, areaName } = this.props
    const { destination, prefecture } = { ...match.params, ...query }
    return {
      query: destination || areaName || prefecture || '東京',
      fields: ['geometry']
    }
  }

  /**
   * 地図のタイルが追加されたときのイベントハンドラ
   */
  onTilesAppended = (tiles) => {
    Promise.all(tiles.map(tile => (
      this.props.actions.search
        .getRoomGroupsForMap(tile, this.query)
        .then(this.triggerRoomGroupsLoaded)
    ))).then(() => {
      if (this.state.highlightedRoomGroup && !this.containsRoomGroup(this.state.highlightedRoomGroup)) {
        this.setState({ highlightedRoomGroup: undefined })
      }
    })
  }

  /**
   * roomGroupマーカークリック時
   */
  onRoomGroupClicked = (roomGroup) => {
    this.setState({ highlightedRoomGroup: roomGroup })
  }

  /**
   * 現在取得済のroomGroupsに含まれるかどうか
   */
  containsRoomGroup(roomGroup) {
    return roomGroup && [...this.props.search.tiles.values()].some(roomGroups => (
      roomGroups.some(({ id }) => roomGroup.id == id)
    ))
  }

  /**
   * roomGroups読込み時にOverlayViewに通知
   * google.maps.eventのカスタムイベントを利用
   */
  triggerRoomGroupsLoaded = (payload) => {
    const map = this.overlayView.getMap()
    if (map) {
      google.maps.event.trigger(map, 'room_groups_loaded', payload)
    }
  }

  renderInfoBox() {
    const { checkin, checkout, rooms, adults } = this.props.query
    const { highlightedRoomGroup: roomGroup } = this.state
    const { t } = this.props
    return roomGroup && (
      <InfoBox
        key={roomGroup.id}
        roomGroup={roomGroup}
        checkin={checkin}
        checkout={checkout}
        rooms={rooms}
        adults={adults}
        highlightedRoomGroup={this.state.highlightedRoomGroup}
        propertyType={this.props.propertyTypes[roomGroup.property.property_type]}
        t={t}
      />
    )
  }

  render() {
    return (
      <React.Fragment>
        {this.props.children}
        <this.ShowGoogleMap
          loadingElement={<div/>}
          containerElement={ <div styleName="container" className="notranslate" /> }
          mapElement={ <div styleName="map" className="notranslate" /> }
          { ...this.props }
        />
      </React.Fragment>
    )
  }
}
